import { getNestedValue } from '../getNestedValue.js';
import { AliasesCSSProperties, StandardCSSProperties } from '../types.js';
import { virtualTheme } from '../virtualTheme.js';
import { MUI_SPACING } from './constants.js';
import { fromThemePaletteStyle } from './from-theme-palette-style.js';
import { stringOrNumberDefined } from './string-or-number-defined.js';

export const aliases: Record<
  keyof AliasesCSSProperties,
  (value: string | number) => {
    className?: string;
    style?: StandardCSSProperties;
  }
> = {
  bgcolor: fromThemePaletteStyle('backgroundColor'),
  m: (value) =>
    stringOrNumberDefined(value, 'margin', (val) => ({
      style: {
        margin: `${val * MUI_SPACING}px`,
      },
    })),

  mt: (value) =>
    stringOrNumberDefined(value, 'marginTop', (val) => ({
      style: {
        marginTop: `${val * MUI_SPACING}px`,
      },
    })),
  mb: (value) =>
    stringOrNumberDefined(value, 'marginBottom', (val) => ({
      style: {
        marginBottom: `${val * MUI_SPACING}px`,
      },
    })),
  ml: (value) =>
    stringOrNumberDefined(value, 'marginLeft', (val) => ({
      style: {
        marginLeft: `${val * MUI_SPACING}px`,
      },
    })),
  mr: (value) =>
    stringOrNumberDefined(value, 'marginRight', (val) => ({
      style: {
        marginRight: `${val * MUI_SPACING}px`,
      },
    })),
  mx: (value) =>
    stringOrNumberDefined(value, ['marginLeft', 'marginRight'], (val) => ({
      style: {
        marginLeft: `${val * MUI_SPACING}px`,
        marginRight: `${val * MUI_SPACING}px`,
      },
    })),
  my: (value) =>
    stringOrNumberDefined(value, ['marginTop', 'marginBottom'], (val) => ({
      style: {
        marginTop: `${val * MUI_SPACING}px`,
        marginBottom: `${val * MUI_SPACING}px`,
      },
    })),
  marginX: (value) =>
    stringOrNumberDefined(value, ['marginLeft', 'marginRight'], (val) => ({
      style: {
        marginLeft: `${val * MUI_SPACING}px`,
        marginRight: `${val * MUI_SPACING}px`,
      },
    })),
  marginY: (value) =>
    stringOrNumberDefined(value, ['marginTop', 'marginBottom'], (val) => ({
      style: {
        marginTop: `${val * MUI_SPACING}px`,
        marginBottom: `${val * MUI_SPACING}px`,
      },
    })),
  p: (value) =>
    stringOrNumberDefined(value, 'padding', (val) => ({
      style: {
        padding: `${val * MUI_SPACING}px`,
      },
    })),
  pt: (value) =>
    stringOrNumberDefined(value, 'paddingTop', (val) => ({
      style: {
        paddingTop: `${val * MUI_SPACING}px`,
      },
    })),
  pb: (value) =>
    stringOrNumberDefined(value, 'paddingBottom', (val) => ({
      style: {
        paddingBottom: `${val * MUI_SPACING}px`,
      },
    })),
  pl: (value) =>
    stringOrNumberDefined(value, 'paddingLeft', (val) => ({
      style: {
        paddingLeft: `${val * MUI_SPACING}px`,
      },
    })),
  pr: (value) =>
    stringOrNumberDefined(value, 'paddingRight', (val) => ({
      style: {
        paddingRight: `${val * MUI_SPACING}px`,
      },
    })),
  px: (value) =>
    stringOrNumberDefined(value, ['paddingLeft', 'paddingRight'], (val) => ({
      style: {
        paddingLeft: `${val * MUI_SPACING}px`,
        paddingRight: `${val * MUI_SPACING}px`,
      },
    })),
  py: (value) =>
    stringOrNumberDefined(value, ['paddingTop', 'paddingBottom'], (val) => ({
      style: {
        paddingTop: `${val * MUI_SPACING}px`,
        paddingBottom: `${val * MUI_SPACING}px`,
      },
    })),
  paddingX: (value) =>
    stringOrNumberDefined(value, ['paddingLeft', 'paddingRight'], (val) => ({
      style: {
        paddingLeft: `${val * MUI_SPACING}px`,
        paddingRight: `${val * MUI_SPACING}px`,
      },
    })),
  paddingY: (value) =>
    stringOrNumberDefined(value, ['paddingTop', 'paddingBottom'], (val) => ({
      style: {
        paddingTop: `${val * MUI_SPACING}px`,
        paddingBottom: `${val * MUI_SPACING}px`,
      },
    })),
  typography: (value) => {
    if (typeof value === 'number') {
      return {};
    }
    const fromTheme = getNestedValue(virtualTheme, `typography.${value}`);
    if (fromTheme) {
      return {
        style: fromTheme,
      };
    }
    return {};
  },
  displayPrint: () => {
    console.warn('displayPrint is not supported!');
    return {
      style: {},
    };
  },
} as const;

export const aliasesKeys = Object.keys(
  aliases,
) as (keyof AliasesCSSProperties)[];
